import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { List } from "react-window";
import { useInfiniteLoader } from "react-window-infinite-loader";

import AssetsWrap from "../../../AssetsWrap";
import SortAssets from "../../../SortAssets";
import AssetListItem from "./AssetListItem";
import useInfinityScroll from "../../../../../hooks/capture/useInfinityScroll";
import { useAssetsCollection } from "../../../../../hooks/capture/useAssetsCollection";
import { useAssetsFilterVariables } from "../../../../../hooks/capture/useAssetsFilterVariables";
import useAssetsFilter from "../../../../../hooks/capture/useAssetsFilter";
import useMutation from "../../../../../hooks/useMutation";
import { UPDATE_PROJECT_MUTATION } from "../../../../../constants/graphql/mutations";
import { getProjectVariables } from "../../../../../utils/profile/settings";

const ITEM_HEIGHT = 100;
const LIST_HEIGHT = 420;
const DEFAULT_REFERENCE_SORT_FIELD = "sku_number";
const DEFAULT_REFERENCE_SORT_DIRECTION = "desc";
const DEFAULT_NON_REFERENCE_ORDER = "item_id.desc";

const getInitialOrder = ({ isReferenceMode, project }) => {
  if (!isReferenceMode) return DEFAULT_NON_REFERENCE_ORDER;

  const catalogPreferences = project?.settings?.catalog_preferences || {};
  const sortField = catalogPreferences.sku_library_sort || DEFAULT_REFERENCE_SORT_FIELD;
  const sortDirection =
    catalogPreferences.sort === "asc" ? "asc" : DEFAULT_REFERENCE_SORT_DIRECTION;

  return `${sortField}.${sortDirection}`;
};

const ItemRow = ({ style, index, items, selectedItems, onToggleItem, project, closeParam, displayItemId, order, showCheckbox }) => {
  const asset = items[index];
  return (
    <AssetListItem
      displayItemId={displayItemId}
      order={order}
      asset={asset}
      style={style}
      project={project}
      closeParam={closeParam}
      selected={selectedItems?.has(asset?.id)}
      onToggle={onToggleItem}
      showCheckbox={showCheckbox}
    />
  );
};

export default function SearchItems({
  project,
  customVariables,
  assetTypeOverride,
  closeParam,
  selectedItems,
  onToggleItem,
  onSelectAll,
  showCheckboxes = true,
  onOpenReceivedItems,
}) {
  let derivedAssetType;
  if (project.importedAssets) {
    derivedAssetType = ["Sku", "Asset"];
  } else if (project.referenceAssets) {
    derivedAssetType = "Sku";
  } else {
    derivedAssetType = "Asset";
  }

  const assetType = assetTypeOverride || derivedAssetType;
  const isReferenceMode = Array.isArray(assetType) || assetType === "Sku";
  const [updateProject] = useMutation(UPDATE_PROJECT_MUTATION);
  const [order, setOrder] = useState(() => getInitialOrder({ isReferenceMode, project }));
  const variables = useAssetsFilterVariables(order);

  useEffect(() => {
    setOrder(getInitialOrder({ isReferenceMode, project }));
  }, [
    isReferenceMode,
    project?.id,
    project?.settings?.catalog_preferences?.sku_library_sort,
    project?.settings?.catalog_preferences?.sort,
  ]);

  if (customVariables) {
    Object.assign(variables, customVariables);
  }

  const { filter, search, setSearch, limit } = useAssetsFilter(variables, assetType);

  const [{ data, loading }, { onLoadMore }] = useAssetsCollection(filter);
  const { collection = [], metadata } = data?.assetsCollection || {};
  const totalCount = metadata?.totalCount || 0;

  const scroll = { entities: collection, totalCount, rowsCount: limit, onLoadMore, loading };
  const { rowCount, loadMoreRows, isRowLoaded } = useInfinityScroll(scroll);
  const onRowsRendered = useInfiniteLoader({ isRowLoaded, loadMoreRows, rowCount });

  const allSelected = useMemo(
    () => collection.length > 0 && collection.every(item => selectedItems?.has(item.id)),
    [collection, selectedItems]
  );

  const someSelected = useMemo(() => collection.some(item => selectedItems?.has(item.id)), [
    collection,
    selectedItems,
  ]);

  const indeterminate = someSelected && !allSelected;

  const handleSetOrder = useCallback(
    nextOrder => {
      setOrder(nextOrder);

      if (!isReferenceMode || !project?.id) return;

      const [nextSortField, nextSortDirectionValue] = nextOrder.split(".");
      const nextSortDirection =
        nextSortDirectionValue === "asc" ? "asc" : DEFAULT_REFERENCE_SORT_DIRECTION;
      const currentCatalogPreferences = project?.settings?.catalog_preferences || {};

      if (
        currentCatalogPreferences.sku_library_sort === nextSortField &&
        currentCatalogPreferences.sort === nextSortDirection
      ) {
        return;
      }

      const variables = getProjectVariables({
        ...project,
        settings: {
          ...(project?.settings || {}),
          catalog_preferences: {
            ...currentCatalogPreferences,
            sku_library_sort: nextSortField,
            sort: nextSortDirection,
          },
        },
      });

      updateProject({ variables });
    },
    [isReferenceMode, project, updateProject]
  );

  const handleSelectAllClick = event => {
    if (onSelectAll) {
      onSelectAll(
        collection.map(item => item.id),
        event.target.checked
      );
    }
  };

  const bulkProps = useMemo(
    () => ({
      label: onSelectAll ? (
        <Checkbox
          checked={allSelected}
          indeterminate={indeterminate}
          onChange={handleSelectAllClick}
          color="primary"
          size="small"
          sx={{ ml: 0.25, mr: 1 }}
          disableRipple
        />
      ) : null,
    }),
    [allSelected, indeterminate, onSelectAll, collection]
  );

  const rowProps = useMemo(
    () => ({
      items: collection,
      selectedItems,
      onToggleItem,
      project,
      closeParam,
      displayItemId: !isReferenceMode,
      order,
      showCheckbox: showCheckboxes,
    }),
    [collection, selectedItems, onToggleItem, project, closeParam, isReferenceMode, order, showCheckboxes]
  );

  // Only show full skeleton state on the initial load (no rows yet). Once we have rows,
  // keep the <List> mounted during fetchMore so react-window preserves its scroll position;
  // the trailing stub rows from useInfinityScroll already convey the "loading more" state.
  const initialLoading = loading && collection.length === 0;
  const showReceivedItemsEmptyCta =
    typeof onOpenReceivedItems === "function" && !initialLoading && collection.length === 0;

  return (
    <AssetsWrap height={undefined} loading={initialLoading} search={search} setSearch={setSearch} hideFilterChips>
      <SortAssets bulkProps={bulkProps} order={order} setOrder={handleSetOrder} />
      <Box display="flex" flexDirection="column" style={{ height: LIST_HEIGHT, overflowY: "auto" }}>
        {showReceivedItemsEmptyCta ? (
          <Box
            display="flex"
            flex={1}
            alignItems="center"
            justifyContent="center"
            px={2}
            py={3}
            minHeight={200}
          >
            <Typography align="center" color="text.secondary" fontSize={17}>
              {"Didn't find your item? "}
              <Link
                component="button"
                type="button"
                fontSize={17}
                onClick={() => onOpenReceivedItems(search)}
                sx={{ fontWeight: 600, verticalAlign: "baseline" }}
              >
                Search for it here
              </Link>
            </Typography>
          </Box>
        ) : (
          <List
            style={{ height: LIST_HEIGHT, width: "100%" }}
            onRowsRendered={onRowsRendered}
            rowCount={rowCount}
            rowHeight={ITEM_HEIGHT}
            rowProps={rowProps}
            rowComponent={ItemRow}
          />
        )}
      </Box>
    </AssetsWrap>
  );
}
